import { supabase } from '../supabaseClient'
import {
  RangoTronco, ModificadoresTronco,
  RangoCuello, ModificadoresCuello,
  CondicionPiernas, ModificadoresPiernas,
  RangoBrazo, ModificadoresBrazo,
  RangoAntebrazo,
  RangoMuneca, ModificadoresMuneca,
  CargaFuerza, CalidadAgarre,
  TipoActividadReba,
  NivelActuacionReba,
} from './reba'

// ===== Evaluaciones REBA por local/puesto de trabajo (multi-empresa) =====
// Mismo patrón que gestion-tecnica-rula.ts / rula_assessments: inputs y
// resultado se guardan como jsonb libre, tipados aquí para que el resto del
// código (formulario, PDF) no maneje `any`.

export interface RebaAssessmentInputs {
  rangoTronco: RangoTronco
  modTronco: ModificadoresTronco
  rangoCuello: RangoCuello
  modCuello: ModificadoresCuello
  condicionPiernas: CondicionPiernas
  modPiernas: ModificadoresPiernas
  rangoBrazo: RangoBrazo
  modBrazo: ModificadoresBrazo
  rangoAntebrazo: RangoAntebrazo
  rangoMuneca: RangoMuneca
  modMuneca: ModificadoresMuneca
  cargaFuerza: CargaFuerza
  fuerzaBrusca: boolean
  calidadAgarre: CalidadAgarre
  tipoActividad: TipoActividadReba
}

// Resultado calculado en RebaMethodologyPage.tsx a partir de src/lib/reba.ts
// — mismo criterio que RulaAssessmentResultado: no existe un
// calculateFullReport() único, esta forma es el contrato de persistencia
// que agrupa las puntuaciones intermedias y finales para guardar/mostrar/imprimir.
export interface RebaAssessmentResultado {
  puntuacionTronco: number
  puntuacionCuello: number
  puntuacionPiernas: number
  grupoA: number
  puntuacionBrazo: number
  puntuacionAntebrazo: number
  puntuacionMuneca: number
  grupoB: number
  puntuacionA: number
  puntuacionB: number
  puntuacionC: number
  puntuacionFinal: number
  nivelActuacion: NivelActuacionReba
}

export interface RebaAssessmentRow {
  id: string
  company_id: string
  job_position_id: string | null
  descripcion: string
  lado: 'izquierdo' | 'derecho'
  inputs: RebaAssessmentInputs
  resultado: RebaAssessmentResultado
  created_by: string | null
  created_at: string
  job_position_name: string | null
}

export interface CreateRebaAssessmentInput {
  companyId: string
  jobPositionId?: string
  descripcion: string
  lado: 'izquierdo' | 'derecho'
  inputs: RebaAssessmentInputs
  resultado: RebaAssessmentResultado
  createdBy: string | null
}

export async function createRebaAssessment(input: CreateRebaAssessmentInput): Promise<void> {
  const { error } = await supabase.from('reba_assessments').insert({
    company_id: input.companyId,
    job_position_id: input.jobPositionId ?? null,
    descripcion: input.descripcion,
    lado: input.lado,
    inputs: input.inputs,
    resultado: input.resultado,
    created_by: input.createdBy,
  })
  if (error) throw error
}

export async function fetchRebaAssessments(companyId: string): Promise<RebaAssessmentRow[]> {
  if (!companyId) return []
  const { data, error } = await supabase
    .from('reba_assessments')
    .select('id, company_id, job_position_id, descripcion, lado, inputs, resultado, created_by, created_at, job_positions(name)')
    .eq('company_id', companyId)
    .order('created_at', { ascending: false })
  if (error) throw error
  return (data ?? []).map((r: any) => ({
    id: r.id,
    company_id: r.company_id,
    job_position_id: r.job_position_id,
    descripcion: r.descripcion,
    lado: r.lado,
    inputs: r.inputs,
    resultado: r.resultado,
    created_by: r.created_by,
    created_at: r.created_at,
    job_position_name: r.job_positions?.name ?? null,
  })) as RebaAssessmentRow[]
}

export async function updateRebaAssessment(id: string, input: CreateRebaAssessmentInput): Promise<void> {
  const { error } = await supabase.from('reba_assessments').update({
    descripcion: input.descripcion,
    lado: input.lado,
    job_position_id: input.jobPositionId ?? null,
    inputs: input.inputs,
    resultado: input.resultado,
  }).eq('id', id)
  if (error) throw error
}

export async function deleteRebaAssessment(id: string): Promise<void> {
  const { error } = await supabase.from('reba_assessments').delete().eq('id', id)
  if (error) throw error
}
